import {
  Button,
  Checkbox,
  Col,
  Divider,
  Flex,
  Modal,
  Row,
  Space,
  Spin,
  Typography,
} from "antd";
import { useEffect, useState } from "react";
import { useToken } from "../../hooks/useToken";
import {
  EIntegrationPlatform,
  TIntegration,
} from "../../models/integrations.model";
import { useTheme } from "../../providers/ThemeProvider";
import {
  getIntegrations,
  updateIntegration,
} from "../../services/integrations.service";
import {
  downloadGooglePhotosItems,
  getGooglePhotosItems,
  refreshGooglePhotos,
  TGooglePhoto,
} from "../../services/integrations/google-photos.service";
import { formatDate } from "../../utils/date";
import { deepCopy } from "../../utils/deepCopy";

export function SettingsGooglePhotos({
  integration,
  onClose,
}: {
  integration: TIntegration;
  onClose: () => void;
}) {
  const { message } = useTheme();
  const [isLoadingMore, setLoadingMore] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(true);
  const [isOpen, setOpen] = useState<boolean>(true);
  const [photos, setPhotos] = useState<TGooglePhoto[]>([]);
  const [pageToken, setPageToken] = useState<string>();
  const [view, setView] = useState<"YEAR" | "MONTH" | "ALL">("YEAR");
  const { token } = useToken();

  const [selectedPhotos, setSelectedPhotos] = useState<TGooglePhoto[]>([]);

  useEffect(() => {
    getGooglePhotosItems({
      onSuccess: (data) => {
        setPhotos(data.mediaItems);
        setPageToken(data.nextPageToken);
        setLoading(false);
      },
      onError: (data) => {
        console.log("on error", data);
        if (data.error?.status == "UNAUTHENTICATED") {
          getIntegrations({
            onSuccess: async (data) => {
              const existingGooglePhotos = data.content.find(
                (integration) =>
                  integration.platform === EIntegrationPlatform.GOOGLE_PHOTOS
              );

              if (existingGooglePhotos) {
                refreshGooglePhotos({
                  accessToken: existingGooglePhotos.refreshToken,
                  onSuccess: (data) => {
                    if (data.access_token) {
                      // Save the new token
                      updateIntegration({
                        integrationId: existingGooglePhotos.id,
                        metadataPublic: {},
                        accessToken: data.access_token,
                        refreshExpireAt: new Date(
                          new Date().getTime() + data.expires_in * 1000
                        ).toISOString(),
                        onSuccess: () => {
                          getGooglePhotosItems({
                            pageToken,
                            onSuccess: (data) => {
                              setPhotos(data.mediaItems);
                              setPageToken(data.nextPageToken);
                              setLoading(false);
                            },
                            onError: () => {
                              setLoading(false);
                            },
                          });
                        },
                      });
                    }
                  },
                });
              }
            },
          });
        }

        setLoading(false);
      },
    });
  }, []);

  const groupedPhotos: { [key: string]: TGooglePhoto[] } = {};

  if (view == "ALL") {
    groupedPhotos["ALL"] = photos;
  } else if (view == "MONTH") {
    // month and year
    photos.forEach((photo) => {
      const Months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];

      const year = new Date(photo.mediaMetadata.creationTime).getFullYear();
      const month = new Date(photo.mediaMetadata.creationTime).getMonth();
      const key = `${Months[month]}, ${year}`;
      if (!groupedPhotos[key]) {
        groupedPhotos[key] = [];
      }
      groupedPhotos[key].push(photo);
    });
  } else {
    // group photos by year
    photos.forEach((photo) => {
      const year = new Date(photo.mediaMetadata.creationTime).getFullYear();
      if (!groupedPhotos[year]) {
        groupedPhotos[year] = [];
      }
      groupedPhotos[year].push(photo);
    });
  }

  return (
    <>
      <Modal
        width="724px"
        style={{
          maxWidth: "95%",
        }}
        styles={{
          content: {
            backgroundColor: `${token.colorBgBase}96`,
            backdropFilter: "blur(30px)",
          },
        }}
        centered
        open={isOpen}
        onCancel={() => {
          setOpen(false);
          setTimeout(() => {
            onClose();
          }, 200);
        }}
        onOk={() => {
          if (selectedPhotos.length == 0) {
            message.error("Please select at least one photo.");
            return;
          }

          getIntegrations({
            onSuccess: async (data) => {
              const existingGooglePhotos = data.content.find(
                (integration) =>
                  integration.platform === EIntegrationPlatform.GOOGLE_PHOTOS
              );

              if (existingGooglePhotos) {
                downloadGooglePhotosItems({
                  items: selectedPhotos.map((photo) => photo.id),
                  accessToken: existingGooglePhotos.accessToken,
                  onSuccess: () => {
                    message.success("Selected photos are being added.");

                    setOpen(false);
                    setTimeout(() => {
                      onClose();
                    }, 200);
                  },
                  onError: () => {
                    message.error("Failed to add selected photos.");
                  },
                });
              }
            },
          });
        }}
        okText={
          selectedPhotos.length == 0
            ? "Select a photo"
            : `Confirm (${selectedPhotos.length}) selected`
        }
      >
        <Typography
          style={{
            fontSize: token.fontSizeHeading4,
            color: token.colorText,
          }}
        >
          Google Photos
        </Typography>
        <Typography
          style={{
            fontSize: token.fontSize,
            color: token.colorText,
          }}
        >
          Select the albums you want to show in Zeitfy.
        </Typography>

        <Flex justify="center" style={{ marginTop: token.margin }}>
          <Space.Compact>
            <Button
              type={view == "YEAR" ? "primary" : "default"}
              onClick={() => setView("YEAR")}
            >
              Years
            </Button>
            <Button
              type={view == "MONTH" ? "primary" : "default"}
              onClick={() => setView("MONTH")}
            >
              Months
            </Button>
            <Button
              type={view == "ALL" ? "primary" : "default"}
              onClick={() => setView("ALL")}
            >
              All Photos
            </Button>
          </Space.Compact>
        </Flex>
        <Divider />
        {isLoading && <Spin size="large" />}
        <div
          style={{
            width: "100%",
            maxHeight: "60vh",
            overflow: "hidden auto",
          }}
        >
          <>
            {Object.keys(groupedPhotos)
              .sort((a, b) => Number(b) - Number(a))
              .map((year, index) => {
                return (
                  <>
                    {year != "ALL" && (
                      <Flex
                        style={{
                          // position: "sticky",
                          // top: 0,
                          // left: 0,
                          // zIndex: 10,
                          marginBottom: token.marginSM,
                          marginTop: index != 0 ? token.marginXXL : 0,
                        }}
                        gap="10px"
                      >
                        {/* <Checkbox /> */}
                        <Typography
                          style={{
                            fontSize: token.fontSize,
                            color: token.colorText,
                          }}
                        >
                          {year}
                        </Typography>
                      </Flex>
                    )}
                    <Row gutter={[12, 12]} key={year}>
                      {groupedPhotos[year]
                        .sort((a, b) => {
                          return (
                            new Date(b.mediaMetadata.creationTime).getTime() -
                            new Date(a.mediaMetadata.creationTime).getTime()
                          );
                        })
                        .map((photo) => {
                          return (
                            <Col key={photo.id} md={6}>
                              <Flex
                                style={{
                                  width: "100%",
                                  position: "relative",
                                  height: "160px",
                                }}
                              >
                                <Flex
                                  align="flex-start"
                                  gap="6px"
                                  style={{
                                    width: "100%",
                                    position: "relative",
                                    zIndex: 1,
                                    padding: token.paddingXS,
                                    borderRadius: "8px 8px 0 0",
                                    background:
                                      "linear-gradient(0deg, rgba(0, 0, 0, 0) 50%, rgba(0, 0, 0, 0.5) 100%),linear-gradient(0deg, rgba(0, 0, 0, 0) 50%, rgba(0, 0, 0, 0.5) 100%)",
                                  }}
                                >
                                  <Checkbox
                                    checked={selectedPhotos.some(
                                      (a) => a.id == photo.id
                                    )}
                                    onChange={(e) =>
                                      setSelectedPhotos((prevState) => {
                                        const copy = deepCopy(prevState);

                                        const index = copy.findIndex(
                                          (a) => a.id == photo.id
                                        );
                                        if (index == -1) {
                                          copy.push(photo);
                                        } else {
                                          copy.splice(index, 1);
                                        }

                                        return copy;
                                      })
                                    }
                                  />

                                  <Typography
                                    style={{
                                      color: token.colorText,
                                      fontSize: token.fontSize,
                                    }}
                                  >
                                    {formatDate(new Date(
                                      photo.mediaMetadata.creationTime
                                    ))}
                                  </Typography>
                                </Flex>

                                <img
                                  src={photo.baseUrl}
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    borderRadius: "8px",
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    zIndex: 0,
                                  }}
                                />
                              </Flex>
                            </Col>
                          );
                        })}
                    </Row>
                  </>
                );
              })}
          </>

          {pageToken && (
            <Button
              loading={isLoadingMore}
              style={{
                width: "100%",
                marginTop: "10px",
              }}
              onClick={() => {
                if (isLoadingMore) return;

                setLoadingMore(true);

                getGooglePhotosItems({
                  pageToken,
                  onSuccess: (data) => {
                    setPhotos((prevState) => {
                      const copy = deepCopy(prevState);

                      copy.push(
                        ...data.mediaItems.filter((photo) => {
                          return copy.findIndex((p) => p.id == photo.id) == -1;
                        })
                      );

                      return copy;
                    });
                    setPageToken(data.nextPageToken);
                    setLoadingMore(false);
                  },
                  onError: () => {
                    setLoadingMore(false);
                  },
                });
              }}
            >
              Load more...
            </Button>
          )}
        </div>
      </Modal>
    </>
  );
}
